import React from 'react';

import { useQuery } from '@apollo/client';
import { Box, Flex, Image, Text } from '@zillow/constellation';

import type { getJumpBackInQuery } from '../../__generated__/graphql';
import { useGlobalStoreState } from '../../contexts/GlobalStoreProvider';
import type { JumpBackInCollection } from '../../fixtures/collections';
import { jumpBackInQuery } from '../../graphql/query';
import { isTruthy } from '../../utils/typescript';
import { Container } from '../Container/Container';
import { JumpBackInCard } from './JumpBackInCard';

const PROPERTY_IMAGE_SIZE = 40;
const getCollectionThumbnail = (collection: JumpBackInCollection) => {
    const photos: string[] = [];
    // Take the first image for the first two properties
    collection?.propertyDetails?.slice(0, 2).forEach((property) => {
        if (property?.photos?.[0]?.url) {
            photos.push(property.photos[0].url);
        }
    });

    return (
        <Flex display="flex">
            {photos.map((photo, index) => (
                <Box
                    key={photo}
                    alt=""
                    as={Image}
                    width={PROPERTY_IMAGE_SIZE}
                    height={PROPERTY_IMAGE_SIZE}
                    sx={{
                        objectFit: 'cover',
                        border: '1px solid $colors.white',
                        borderRadius: 'calc($radii.sm / 2)',
                        marginLeft: `-${index * (PROPERTY_IMAGE_SIZE / 3)}px`,
                    }}
                    src={photo}
                />
            ))}
        </Flex>
    );
};
export const JumpBackIn: React.FC = () => {
    const { isJumpBackInEnabled } = useGlobalStoreState();

    const { loading, data } = useQuery<getJumpBackInQuery>(jumpBackInQuery, {
        skip: !isJumpBackInEnabled,
    });

    if (!isJumpBackInEnabled) {
        return null;
    }

    return (
        <Container gap="md">
            <Container gap={0}>
                <React.Fragment>
                    {/* TODO: https://zillowgroup.atlassian.net/browse/HOPS-358 */}
                    <Text fontType="h5" fontFamily="serif">
                        Beth, jump back in
                    </Text>
                    <Text fontType="bodySmall" fontFamily="sansSerif" fontColor="gray500">
                        These suggestions are based on your recent activity on Zillow
                    </Text>
                </React.Fragment>
            </Container>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '$spacing.sm',
                    overflowX: 'auto',
                    scrollbarWidth: 'none',
                    padding: '$spacing.sm $spacing.xs',
                    marginLeft: 'calc($spacing.xs * -1)',
                }}
            >
                {loading ? (
                    <React.Fragment>
                        <JumpBackInCard loading />
                        <JumpBackInCard loading />
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        {data?.collections?.collections?.filter(isTruthy).map((collection) => (
                            <JumpBackInCard
                                key={collection.name}
                                leftContent={getCollectionThumbnail(collection)}
                                href={collection.link?.url ?? ''}
                                title={collection.name}
                                body={collection.caption ?? ''}
                            />
                        ))}
                        {/*  TODO: Add MiSO card https://zillowgroup.atlassian.net/browse/HOPS-337 */}
                    </React.Fragment>
                )}
            </Box>
        </Container>
    );
};
